/**
 * DatasetActionDialog — Inteligência de Dados (Fase 4)
 * -----------------------------------------------------------------------------
 * Modal de "Ações em lote" sobre o resultado do Explorador ou de uma comparação.
 * O usuário escolhe a ação (criar contatos, clientes, veículos, itens de lista
 * de campanha ou tarefas), mapeia os campos do dataset e confirma.
 *
 * Fluxo: seleção -> prévia de impacto -> confirmar -> fila (worker assíncrono).
 * Nada é processado no navegador: a UI apenas cria o lote e acompanha.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 4 - Ações em lote).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Rocket } from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import { useExternalDatasetFields } from '@/hooks/data/useExternalDatasets';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import {
  EXTERNAL_ACTION_LABELS,
  useExternalActionMutations,
  useExternalActionPreview,
  type ExternalActionSelection,
  type ExternalActionType,
} from '@/hooks/data/useExternalActions';

interface DatasetActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasetId: string | null;
  datasetName?: string | null;
  /** Seleção que originou o lote (filtro do explorador ou comparação) */
  selection: ExternalActionSelection;
}

/** Campos de mapeamento exigidos/opcionais por ação */
const FIELD_MAP: Record<
  ExternalActionType,
  { key: string; label: string; required?: boolean }[]
> = {
  create_contact: [
    { key: 'phone_path', label: 'Telefone', required: true },
    { key: 'name_path', label: 'Nome' },
    { key: 'email_path', label: 'E-mail' },
  ],
  create_customer: [
    { key: 'document_path', label: 'CPF / CNPJ', required: true },
    { key: 'name_path', label: 'Nome / Razão social', required: true },
    { key: 'phone_path', label: 'Telefone' },
    { key: 'email_path', label: 'E-mail' },
  ],
  create_vehicle: [
    { key: 'plate_path', label: 'Placa', required: true },
    { key: 'document_path', label: 'CPF / CNPJ do cliente' },
    { key: 'brand_path', label: 'Marca' },
    { key: 'model_path', label: 'Modelo' },
    { key: 'year_path', label: 'Ano' },
  ],
  add_to_campaign_list: [
    { key: 'phone_path', label: 'Telefone', required: true },
    { key: 'name_path', label: 'Nome' },
    { key: 'email_path', label: 'E-mail' },
  ],
  create_task: [
    { key: 'title_path', label: 'Título (campo do dataset)' },
    { key: 'phone_path', label: 'Telefone (vincula contato)' },
  ],
};

/** Modal de criação de lote de ação */
export function DatasetActionDialog({
  open,
  onOpenChange,
  datasetId,
  datasetName,
  selection,
}: DatasetActionDialogProps) {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const { fields } = useExternalDatasetFields(datasetId);
  const { createBatch } = useExternalActionMutations();

  const [actionType, setActionType] = useState<ExternalActionType>('create_contact');
  const [name, setName] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [listId, setListId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [phaseId, setPhaseId] = useState('');

  // Listas de campanha prontas (mesma consulta usada na criação de campanhas)
  const { data: campaignLists = [] } = useQuery({
    queryKey: ['external-action-campaign-lists', currentTenant?.id],
    enabled: open && actionType === 'add_to_campaign_list' && Boolean(currentTenant?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_lists')
        .select('id, name, total_rows')
        .eq('tenant_id', currentTenant!.id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Categorias/fases usadas apenas na ação "criar tarefas"
  const { categories } = useTaskCategories(true, 'normal');
  const { phases } = useTaskPhases(categoryId || undefined, 'normal');

  const { total, isLoading: loadingPreview, error: previewError } = useExternalActionPreview({
    datasetId,
    selection,
    enabled: open,
  });

  // Reinicia o formulário sempre que o modal abre
  useEffect(() => {
    if (!open) return;
    setActionType('create_contact');
    setName('');
    setMapping({});
    setListId('');
    setCategoryId('');
    setPhaseId('');
  }, [open]);

  const requiredFields = FIELD_MAP[actionType];

  /** Validação local (o backend revalida tudo) */
  const missing = useMemo(() => {
    const pending = requiredFields
      .filter((f) => f.required && !mapping[f.key])
      .map((f) => f.label);
    if (actionType === 'add_to_campaign_list' && !listId) pending.push('Lista de campanha');
    if (actionType === 'create_task' && (!categoryId || !phaseId)) pending.push('Categoria e fase');
    return pending;
  }, [requiredFields, mapping, actionType, listId, categoryId, phaseId]);

  /** Cria o lote e leva o usuário para o acompanhamento */
  const handleConfirm = async () => {
    if (!datasetId || missing.length > 0) return;

    const config: Record<string, unknown> = { ...mapping };
    if (actionType === 'add_to_campaign_list') config.list_id = listId;
    if (actionType === 'create_task') {
      config.category_id = categoryId;
      config.phase_id = phaseId;
      config.task_type = 'normal';
    }

    try {
      await createBatch.mutateAsync({
        datasetId,
        actionType,
        name: name.trim() || EXTERNAL_ACTION_LABELS[actionType],
        config,
        selection,
      });
      toast.success('Lote criado — o processamento roda em segundo plano.');
      onOpenChange(false);
      navigate('/tenant/data/actions');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar o lote de ação');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px] w-[95vw] max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
        <PremiumDialogHeader
          icon={Rocket}
          title="Ação em lote"
          description={datasetName ? `Dataset: ${datasetName}` : 'Ações sobre o resultado'}
        />

        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
          {/* Impacto da seleção */}
          <InlineNotice variant={total > 0 ? 'info' : 'warning'}>
            {loadingPreview
              ? 'Calculando o impacto da seleção...'
              : `${total.toLocaleString('pt-BR')} registro(s) serão processados. A seleção é congelada agora — reimportações posteriores não alteram o resultado.`}
          </InlineNotice>

          {previewError && <InlineNotice variant="error">{previewError.message}</InlineNotice>}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Ação</Label>
              <Select
                value={actionType}
                onValueChange={(v) => {
                  setActionType(v as ExternalActionType);
                  setMapping({});
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(EXTERNAL_ACTION_LABELS) as ExternalActionType[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {EXTERNAL_ACTION_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Nome do lote</Label>
              <Input
                value={name}
                maxLength={120}
                placeholder={EXTERNAL_ACTION_LABELS[actionType]}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          {/* Destino específico da ação */}
          {actionType === 'add_to_campaign_list' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Lista de campanha</Label>
              <Select value={listId} onValueChange={setListId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a lista" />
                </SelectTrigger>
                <SelectContent>
                  {campaignLists.map((list) => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name} ({Number(list.total_rows ?? 0).toLocaleString('pt-BR')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {actionType === 'create_task' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Categoria</Label>
                <Select
                  value={categoryId}
                  onValueChange={(v) => {
                    setCategoryId(v);
                    setPhaseId('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category: { id: string; name: string }) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fase inicial</Label>
                <Select value={phaseId} onValueChange={setPhaseId} disabled={!categoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a fase" />
                  </SelectTrigger>
                  <SelectContent>
                    {phases.map((phase: { id: string; name: string }) => (
                      <SelectItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Mapeamento de campos do dataset */}
          <div className="space-y-2">
            <Label className="text-xs">Mapeamento dos campos</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              {requiredFields.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    {field.label}
                    {field.required && <span className="text-destructive"> *</span>}
                  </Label>
                  <Select
                    value={mapping[field.key] ?? ''}
                    onValueChange={(v) =>
                      setMapping((prev) => ({ ...prev, [field.key]: v === '__none__' ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o campo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Não usar —</SelectItem>
                      {fields.map((datasetField) => (
                        <SelectItem key={datasetField.id} value={datasetField.json_path}>
                          {datasetField.label || datasetField.json_path}
                          {datasetField.is_indexed ? ' ⚡' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {missing.length > 0 && (
            <InlineNotice variant="warning">
              Informe: {missing.join(', ')}.
            </InlineNotice>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t p-3">
          <Badge variant="secondary">
            {total.toLocaleString('pt-BR')} registro(s) na seleção
          </Badge>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              className="gap-1.5"
              disabled={!datasetId || total === 0 || missing.length > 0 || createBatch.isPending}
              onClick={handleConfirm}
            >
              {createBatch.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4" />
              )}
              Confirmar ação
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
